import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { collection, doc as firestoreDoc, getDoc, getDocs, query, where } from "firebase/firestore";
import * as XLSX from "xlsx";
import db from "../Firebase";

const allowedLabels = ["Learn", "Grow", "Guidance", "Finance", "Membership", "Manage", "Community"];

const categoryAliases = {
    "Learn Page": "Learn",
    "Grow Section": "Grow",
    "Finance Home": "Finance",
};

const phaseConfig = {
    "1": {
        label: "Phase 1",
        from: "2025-12-31",
        displayFrom: "31 Dec 2025",
    },
    "2": {
        label: "Phase 2",
        from: "2026-04-09",
        displayFrom: "09 Apr 2026",
    },
};

const parseDateValue = (val) => {
    if (!val) return null;
    if (typeof val?.toDate === "function") return val.toDate();
    if (val instanceof Date) return val;
    if (typeof val?.seconds === "number") return new Date(val.seconds * 1000);
    const d = new Date(val);
    return isNaN(d) ? null : d;
};

const fmtDate = (val) => {
    const d = parseDateValue(val);
    if (!d) return val ? String(val) : "-";
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const toTitleCase = (str) => {
    if (!str) return "-";
    return String(str).toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
};

const cleanText = (val, fallback = "-") => {
    if (val === null || val === undefined || val === "") return fallback;
    return String(val);
};

const compactPhone = (phone) => String(phone || "").replace(/\D/g, "").slice(-10);

const normalizePhase = (phase) => {
    const clean = cleanText(phase, "").toLowerCase().trim();
    if (clean === "1" || clean === "phase 1") return "1";
    if (clean === "2" || clean === "phase 2") return "2";
    return null;
};

const phaseStartDate = (phaseKey) => new Date(`${phaseConfig[phaseKey].from}T00:00:00`);

const afterPhaseStart = (val, phaseKey) => {
    const d = parseDateValue(val);
    return d && d >= phaseStartDate(phaseKey);
};

const normalizeModule = (visit = {}) => {
    const pageName = cleanText(visit.page_name, "");
    const category = cleanText(visit.category, "");
    const aliased = categoryAliases[category] || categoryAliases[pageName];
    if (aliased) return aliased;
    if (allowedLabels.includes(pageName)) return pageName;
    if (allowedLabels.includes(category)) return category;
    return allowedLabels.find((label) => category.includes(label) || pageName.includes(label)) || null;
};

const createEmptyModuleCounts = () => allowedLabels.reduce((acc, label) => {
    acc[label] = 0;
    return acc;
}, {});

const topEntries = (countMap, limit = 3) => Object.entries(countMap)
    .filter(([, count]) => count > 0)
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .slice(0, limit);

const batchedIn = async (collName, field, values) => {
    const cleanValues = [...new Set(values.filter(Boolean))];
    if (cleanValues.length === 0) return [];

    const chunks = [];
    for (let i = 0; i < cleanValues.length; i += 10) chunks.push(cleanValues.slice(i, i + 10));

    const snaps = await Promise.all(
        chunks.map((chunk) => getDocs(query(collection(db, collName), where(field, "in", chunk))))
    );
    return snaps.flatMap((snap) => snap.docs);
};

function ParticipantsPostActivity() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [funderFilter, setFunderFilter] = useState("all");
    const [courseModal, setCourseModal] = useState(null);
    const [workshopModal, setWorkshopModal] = useState(null);
    const [ordersModal, setOrdersModal] = useState(null);
    const [activityModal, setActivityModal] = useState(null);
    const [activePhase, setActivePhase] = useState("1");

    useEffect(() => {
        const fetchAll = async () => {
            setLoading(true);

            try {
                const funderSnap = await getDocs(collection(db, "funder_users_list"));
                const rowMap = {};

                funderSnap.docs.forEach((docSnap) => {
                    const d = docSnap.data();
                    const phone = compactPhone(d.phone_no || d.phone || docSnap.id);
                    const phaseKey = normalizePhase(d.phase);
                    if (!phone || !phaseKey || rowMap[phone]) return;

                    rowMap[phone] = {
                        id: docSnap.id,
                        name: cleanText(d.name),
                        phone,
                        username: cleanText(d.username),
                        funder: cleanText(d.funder),
                        phase: phaseConfig[phaseKey].label,
                        phaseKey,
                        dataFrom: phaseConfig[phaseKey].displayFrom,
                        dateOfJoining: d.created_date || null,
                        ageRange: d.age_range || null,
                        profileType: d.profile_type || null,
                        city: d.city || null,
                        state: d.state || null,
                        workshops: 0,
                        workshopDetails: [],
                        courses: 0,
                        courseDetails: [],
                        orders: 0,
                        orderDetails: [],
                        portfolio: false,
                        rgApplied: false,
                        activityVisits: 0,
                        activityDetails: [],
                        moduleCounts: createEmptyModuleCounts(),
                        topModule: "-",
                        topInterests: "-",
                        totalOpens: 0,
                        totalDuration: 0,
                        lastActive: null,
                    };
                });

                const phones = Object.keys(rowMap);

                const pdDocs = await batchedIn("personal_details", "phone_no", phones);
                pdDocs.forEach((docSnap) => {
                    const d = docSnap.data();
                    const phone = compactPhone(d.phone_no);
                    if (!rowMap[phone]) return;
                    rowMap[phone].name = rowMap[phone].name !== "-" ? cleanText(rowMap[phone].name) : cleanText(d.full_name);
                    rowMap[phone].dateOfJoining = rowMap[phone].dateOfJoining || d.created_date || null;
                });

                const ptDocs = await batchedIn("profile_type", "phone_no", phones);
                ptDocs.forEach((docSnap) => {
                    const d = docSnap.data();
                    const phone = compactPhone(d.phone_no);
                    if (!rowMap[phone]) return;
                    rowMap[phone].ageRange = rowMap[phone].ageRange || d.age_range || null;
                    rowMap[phone].profileType = rowMap[phone].profileType || d.profile_type || null;
                });

                const wrDocs = await batchedIn("workshop_registration", "phone", phones);
                wrDocs.forEach((docSnap) => {
                    const d = docSnap.data();
                    const phone = compactPhone(d.phone);
                    if (rowMap[phone] && afterPhaseStart(d.created_date, rowMap[phone].phaseKey)) {
                        rowMap[phone].workshops += 1;
                        rowMap[phone].workshopDetails.push({
                            phone,
                            workshop_event_name: d.workshop_event_name || d.event_name || d.name || "-",
                            created_date: d.created_date,
                        });
                    }
                });

                const ecDocs = await batchedIn("nv_enrolled_courses", "phone_no", phones);
                ecDocs.forEach((docSnap) => {
                    const d = docSnap.data();
                    const phone = compactPhone(d.phone_no);
                    if (rowMap[phone] && afterPhaseStart(d.created_date, rowMap[phone].phaseKey)) {
                        rowMap[phone].courses += 1;
                        rowMap[phone].courseDetails.push({
                            phone_no: phone,
                            course_name: d.course_name || "-",
                            created_date: d.created_date,
                        });
                    }
                });

                const orderDocs = await batchedIn("nv_orders_data", "phone_no", phones);
                orderDocs.forEach((docSnap) => {
                    const d = docSnap.data();
                    const phone = compactPhone(d.phone_no);
                    if (rowMap[phone] && afterPhaseStart(d.order_date, rowMap[phone].phaseKey)) {
                        rowMap[phone].orders += 1;
                        rowMap[phone].orderDetails.push({
                            phone_no: phone,
                            order_date: d.order_date,
                            service_name: d.service_name || "-",
                        });
                    }
                });

                const pfDocs = await batchedIn("nv_portfolio_details", "phone_no", phones);
                pfDocs.forEach((docSnap) => {
                    const phone = compactPhone(docSnap.data().phone_no);
                    if (rowMap[phone]) rowMap[phone].portfolio = true;
                });

                const loanDocs = await batchedIn("nv_loan", "username", phones);
                loanDocs.forEach((docSnap) => {
                    const phone = compactPhone(docSnap.data().username);
                    if (rowMap[phone]) rowMap[phone].rgApplied = true;
                });

                const pnDocs = await batchedIn("parlour_name", "phone", phones);
                pnDocs.forEach((docSnap) => {
                    const d = docSnap.data();
                    const phone = compactPhone(d.phone);
                    if (!rowMap[phone]) return;
                    rowMap[phone].city = rowMap[phone].city || d.city || null;
                    rowMap[phone].state = rowMap[phone].state || d.state || null;
                });

                const activityDocs = await Promise.all(
                    phones.map(async (phone) => {
                        const snap = await getDoc(firestoreDoc(db, "users_activity", phone));
                        return snap.exists() ? { phone, data: snap.data() } : null;
                    })
                );

                activityDocs.filter(Boolean).forEach(({ phone, data }) => {
                    if (!rowMap[phone]) return;

                    const moduleCounts = createEmptyModuleCounts();
                    const interestCounts = {};
                    const activityDetails = [];
                    const visits = Array.isArray(data.page_visits) ? data.page_visits : [];

                    visits.forEach((visit) => {
                        if (!afterPhaseStart(visit.visit_time, rowMap[phone].phaseKey)) return;

                        const module = normalizeModule(visit);
                        if (!module) return;

                        moduleCounts[module] += 1;
                        const category = cleanText(visit.category);
                        interestCounts[category] = (interestCounts[category] || 0) + 1;
                        activityDetails.push({
                            module,
                            category,
                            description: cleanText(visit.description),
                            page_name: cleanText(visit.page_name),
                            visit_time: visit.visit_time,
                        });
                    });

                    const topModule = topEntries(moduleCounts, 1)[0];
                    const topInterestText = topEntries(interestCounts, 3)
                        .map(([label, count]) => `${label} (${count})`)
                        .join(", ");

                    rowMap[phone].activityVisits = activityDetails.length;
                    rowMap[phone].activityDetails = activityDetails.sort((a, b) => {
                        const left = parseDateValue(b.visit_time)?.getTime() || 0;
                        const right = parseDateValue(a.visit_time)?.getTime() || 0;
                        return left - right;
                    });
                    rowMap[phone].moduleCounts = moduleCounts;
                    rowMap[phone].topModule = topModule ? `${topModule[0]} (${topModule[1]})` : "-";
                    rowMap[phone].topInterests = topInterestText || "-";
                    rowMap[phone].totalOpens = data.total_opens || 0;
                    rowMap[phone].totalDuration = data.total_duration || 0;
                    rowMap[phone].lastActive = data.last_active || null;
                });

                setRows(Object.values(rowMap).sort((a, b) => (
                    a.phaseKey.localeCompare(b.phaseKey) || cleanText(a.name).localeCompare(cleanText(b.name))
                )));
            } catch (err) {
                console.error("ParticipantsPostActivity fetch error:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchAll();
    }, []);

    const funderOptions = useMemo(
        () => [...new Set(rows.map((row) => row.funder).filter((v) => v && v !== "-"))].sort(),
        [rows]
    );

    const filteredRows = useMemo(
        () => rows.filter((row) => {
            if (funderFilter !== "all" && row.funder !== funderFilter) return false;
            return true;
        }),
        [rows, funderFilter]
    );

    const groupedRows = useMemo(() => ({
        "1": filteredRows.filter((row) => row.phaseKey === "1"),
        "2": filteredRows.filter((row) => row.phaseKey === "2"),
    }), [filteredRows]);

    const renderCount = (items, field) => items.reduce((sum, row) => sum + row[field], 0);

    const renderTable = (items, startIndex = 0) => (
        <div className="table-responsive">
            <table className="table table-bordered table-hover align-middle mb-0" style={{ fontSize: "13px" }}>
                <thead className="table-dark">
                    <tr>
                        <th>#</th>
                        <th>Name</th>
                        <th>Phone</th>
                        <th>Username</th>
                        <th>Funder</th>
                        <th>Phase</th>
                        <th>Data From</th>
                        <th>Age Range</th>
                        <th>Profile Type</th>
                        <th>City</th>
                        <th>State</th>
                        <th>Date of Joining</th>
                        <th className="text-center">Workshops</th>
                        <th className="text-center">Courses</th>
                        <th className="text-center">Orders</th>
                        <th className="text-center">Activity</th>
                        {allowedLabels.map((label) => (
                            <th key={label} className="text-center">{label}</th>
                        ))}
                        <th>Top Module</th>
                        <th>Last Active</th>
                        <th className="text-center">Portfolio</th>
                        <th className="text-center">RG Applied</th>
                    </tr>
                </thead>
                <tbody>
                    {items.length === 0 ? (
                        <tr><td colSpan="27" className="text-center text-muted">No data found</td></tr>
                    ) : items.map((row, index) => (
                        <tr key={row.phone}>
                            <td>{startIndex + index + 1}</td>
                            <td>{toTitleCase(row.name)}</td>
                            <td>{row.phone}</td>
                            <td>{row.username}</td>
                            <td>{row.funder}</td>
                            <td>{row.phase}</td>
                            <td>{row.dataFrom}</td>
                            <td>{row.ageRange || "-"}</td>
                            <td>{row.profileType || "-"}</td>
                            <td>{row.city || "-"}</td>
                            <td>{row.state || "-"}</td>
                            <td>{fmtDate(row.dateOfJoining)}</td>
                            <td className="text-center">
                                {row.workshops > 0 ? (
                                    <span className="badge bg-primary" style={{ cursor: "pointer" }} onClick={() => setWorkshopModal({ phone: row.phone, items: sortByDateDesc(row.workshopDetails) })}>
                                        {row.workshops}
                                    </span>
                                ) : <span className="text-muted">-</span>}
                            </td>
                            <td className="text-center">
                                {row.courses > 0 ? (
                                    <span className="badge bg-info text-dark" style={{ cursor: "pointer" }} onClick={() => setCourseModal({ phone: row.phone, items: sortByDateDesc(row.courseDetails) })}>
                                        {row.courses}
                                    </span>
                                ) : <span className="text-muted">-</span>}
                            </td>
                            <td className="text-center">
                                {row.orders > 0 ? (
                                    <span className="badge bg-warning text-dark" style={{ cursor: "pointer" }} onClick={() => setOrdersModal({ phone: row.phone, items: sortByOrderDateDesc(row.orderDetails) })}>
                                        {row.orders}
                                    </span>
                                ) : <span className="text-muted">-</span>}
                            </td>
                            <td className="text-center">
                                {row.activityVisits > 0 ? (
                                    <span
                                        className="badge bg-dark"
                                        style={{ cursor: "pointer" }}
                                        onClick={() => setActivityModal({ phone: row.phone, items: row.activityDetails })}
                                    >
                                        {row.activityVisits}
                                    </span>
                                ) : <span className="text-muted">-</span>}
                            </td>
                            {allowedLabels.map((label) => (
                                <td key={label} className="text-center">
                                    {row.moduleCounts?.[label] > 0 ? row.moduleCounts[label] : <span className="text-muted">-</span>}
                                </td>
                            ))}
                            <td>{row.topModule}</td>
                            <td>{fmtDate(row.lastActive)}</td>
                            <td className="text-center">{row.portfolio ? <span className="badge bg-success">Yes</span> : <span className="badge bg-secondary">No</span>}</td>
                            <td className="text-center">{row.rgApplied ? <span className="badge bg-success">Yes</span> : <span className="badge bg-secondary">No</span>}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );

    const handleExport = () => {
        const summaryData = filteredRows.map((row, i) => ({
            "#": i + 1,
            Name: toTitleCase(row.name),
            Phone: row.phone,
            Username: row.username,
            Funder: row.funder || "-",
            Phase: row.phase || "-",
            "Data From": row.dataFrom,
            "Age Range": row.ageRange || "-",
            "Profile Type": row.profileType || "-",
            City: row.city || "-",
            State: row.state || "-",
            "Date of Joining": fmtDate(row.dateOfJoining),
            Workshops: row.workshops,
            Courses: row.courses,
            Orders: row.orders,
            "Activity Visits": row.activityVisits,
            ...allowedLabels.reduce((acc, label) => {
                acc[label] = row.moduleCounts?.[label] || 0;
                return acc;
            }, {}),
            "Top Module": row.topModule,
            "Top Interests": row.topInterests,
            "Last Active": fmtDate(row.lastActive),
            "Total Opens": row.totalOpens,
            "Total Duration": row.totalDuration,
            Portfolio: row.portfolio ? "Yes" : "No",
            "RG Applied": row.rgApplied ? "Yes" : "No",
        }));

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(summaryData), "Summary");

        const activityData = [];
        filteredRows.forEach((row) => {
            row.activityDetails.forEach((item) => {
                activityData.push({
                    Phone: row.phone,
                    Name: toTitleCase(row.name),
                    Phase: row.phase,
                    "Data From": row.dataFrom,
                    Module: item.module,
                    Category: item.category,
                    Page: item.page_name,
                    Description: item.description,
                    "Visit Time": fmtDate(item.visit_time),
                });
            });
        });
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(activityData.length ? activityData : [{}]), "Activity Details");

        XLSX.writeFile(wb, "participants_post_activity.xlsx");
    };

    const sortByDateDesc = (items) => [...items].sort((a, b) => {
        const left = parseDateValue(b.created_date)?.getTime() || 0;
        const right = parseDateValue(a.created_date)?.getTime() || 0;
        return left - right;
    });

    const sortByOrderDateDesc = (items) => [...items].sort((a, b) => {
        const left = parseDateValue(b.order_date)?.getTime() || 0;
        const right = parseDateValue(a.order_date)?.getTime() || 0;
        return left - right;
    });

    const filterLabelStyle = {
        display: "block",
        marginBottom: "7px",
        color: "#5f6773",
        fontSize: "12px",
        fontWeight: 700,
        letterSpacing: "0.02em",
        textTransform: "uppercase",
        lineHeight: 1,
    };

    const inputStyle = {
        height: "46px",
        borderRadius: "8px",
        border: "1px solid #ced4da",
        fontSize: "15px",
        padding: "9px 13px",
        boxShadow: "none",
    };

    return (
        <>
            <div className="container-fluid py-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <div>
                        <h3 className="mb-1">Participants Post Activity</h3>
                        <div className="text-muted" style={{ fontSize: "13px" }}>
                            Phase 1 from 31 Dec 2025, Phase 2 from 09 Apr 2026.
                        </div>
                    </div>
                    <Link to="/" className="btn btn-outline-secondary btn-sm">Back</Link>
                </div>

                <div className="card mb-3">
                    <div className="card-body" style={{ padding: "22px 24px" }}>
                        <div
                            style={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: "18px 24px",
                                alignItems: "flex-end",
                            }}
                        >
                            <div style={{ minWidth: "260px", flex: "1 1 260px" }}>
                                <span style={filterLabelStyle}>Funder</span>
                                <select className="form-select" style={inputStyle} value={funderFilter} onChange={(e) => setFunderFilter(e.target.value)}>
                                    <option value="all">All</option>
                                    {funderOptions.map((funder) => <option key={funder} value={funder}>{funder}</option>)}
                                </select>
                            </div>
                            <div style={{ minWidth: "280px", flex: "1 1 280px" }}>
                                <span style={filterLabelStyle}>Cohort Windows</span>
                                <div className="d-flex flex-wrap gap-2">
                                    <span className="badge bg-primary" style={{ fontSize: "13px", padding: "10px 12px" }}>Phase 1: after 31 Dec 2025</span>
                                    <span className="badge bg-info text-dark" style={{ fontSize: "13px", padding: "10px 12px" }}>Phase 2: after 09 Apr 2026</span>
                                </div>
                            </div>
                            <div
                                style={{
                                    display: "flex",
                                    gap: "12px",
                                    flex: "0 1 auto",
                                    minWidth: "160px",
                                    paddingTop: "19px",
                                }}
                            >
                                <button className="btn btn-success" style={{ height: "46px", minWidth: "150px", borderRadius: "8px", fontWeight: 700 }} onClick={handleExport} disabled={loading || filteredRows.length === 0}>Export Excel</button>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="row g-3 mb-3">
                    <div className="col-md-3">
                        <div className="card"><div className="card-body"><div className="text-muted">Total</div><h4>{filteredRows.length}</h4></div></div>
                    </div>
                    <div className="col-md-3">
                        <div className="card"><div className="card-body"><div className="text-muted">Workshops</div><h4>{renderCount(filteredRows, "workshops")}</h4></div></div>
                    </div>
                    <div className="col-md-3">
                        <div className="card"><div className="card-body"><div className="text-muted">Courses</div><h4>{renderCount(filteredRows, "courses")}</h4></div></div>
                    </div>
                    <div className="col-md-3">
                        <div className="card"><div className="card-body"><div className="text-muted">Orders</div><h4>{renderCount(filteredRows, "orders")}</h4></div></div>
                    </div>
                </div>

                {loading ? (
                    <div className="text-center py-5">Loading...</div>
                ) : (
                    <div className="card mb-4">
                        <div className="card-header bg-white">
                            <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
                                <div className="btn-group" role="group" aria-label="Phase tabs">
                                    {["1", "2"].map((phaseKey) => (
                                        <button
                                            key={phaseKey}
                                            type="button"
                                            className={`btn ${activePhase === phaseKey ? "btn-primary" : "btn-outline-primary"}`}
                                            onClick={() => setActivePhase(phaseKey)}
                                            style={{ minWidth: "150px", fontWeight: 700 }}
                                        >
                                            {phaseConfig[phaseKey].label}
                                            <span className={`ms-2 badge ${activePhase === phaseKey ? "bg-light text-primary" : "bg-primary"}`}>
                                                {groupedRows[phaseKey].length}
                                            </span>
                                        </button>
                                    ))}
                                </div>
                                <div className="d-flex gap-2">
                                    <span className="badge bg-secondary" style={{ fontSize: "13px", padding: "9px 11px" }}>
                                        Data after {phaseConfig[activePhase].displayFrom}
                                    </span>
                                    <span className="badge bg-dark" style={{ fontSize: "13px", padding: "9px 11px" }}>
                                        {renderCount(groupedRows[activePhase], "activityVisits")} Visits
                                    </span>
                                </div>
                            </div>
                        </div>
                        {renderTable(groupedRows[activePhase], activePhase === "1" ? 0 : groupedRows["1"].length)}
                    </div>
                )}
            </div>

            {workshopModal && (
                <DetailModal title="Workshop Registrations" phone={workshopModal.phone} onClose={() => setWorkshopModal(null)}>
                    <table className="table table-sm table-bordered align-middle mb-0" style={{ fontSize: "13px" }}>
                        <thead className="table-dark"><tr><th>#</th><th>Phone</th><th>Workshop / Event</th><th>Date</th></tr></thead>
                        <tbody>
                            {workshopModal.items.map((item, i) => (
                                <tr key={i}><td>{i + 1}</td><td>{item.phone}</td><td>{item.workshop_event_name}</td><td>{fmtDate(item.created_date)}</td></tr>
                            ))}
                        </tbody>
                    </table>
                </DetailModal>
            )}

            {courseModal && (
                <DetailModal title="Enrolled Courses" phone={courseModal.phone} onClose={() => setCourseModal(null)}>
                    <table className="table table-sm table-bordered align-middle mb-0" style={{ fontSize: "13px" }}>
                        <thead className="table-dark"><tr><th>#</th><th>Phone</th><th>Course Name</th><th>Date</th></tr></thead>
                        <tbody>
                            {courseModal.items.map((item, i) => (
                                <tr key={i}><td>{i + 1}</td><td>{item.phone_no}</td><td>{item.course_name}</td><td>{fmtDate(item.created_date)}</td></tr>
                            ))}
                        </tbody>
                    </table>
                </DetailModal>
            )}

            {ordersModal && (
                <DetailModal title="Orders" phone={ordersModal.phone} onClose={() => setOrdersModal(null)}>
                    <table className="table table-sm table-bordered align-middle mb-0" style={{ fontSize: "13px" }}>
                        <thead className="table-dark"><tr><th>#</th><th>Phone</th><th>Order Date</th><th>Service Name</th></tr></thead>
                        <tbody>
                            {ordersModal.items.map((item, i) => (
                                <tr key={i}><td>{i + 1}</td><td>{item.phone_no}</td><td>{fmtDate(item.order_date)}</td><td>{item.service_name}</td></tr>
                            ))}
                        </tbody>
                    </table>
                </DetailModal>
            )}

            {activityModal && (
                <DetailModal title="Module Activity" phone={activityModal.phone} onClose={() => setActivityModal(null)} maxWidth="760px">
                    <table className="table table-sm table-bordered align-middle mb-0" style={{ fontSize: "13px" }}>
                        <thead className="table-dark">
                            <tr><th>#</th><th>Module</th><th>Category / Interest</th><th>Page</th><th>Description</th><th>Visit Time</th></tr>
                        </thead>
                        <tbody>
                            {activityModal.items.map((item, i) => (
                                <tr key={i}>
                                    <td>{i + 1}</td>
                                    <td><span className="badge bg-primary">{item.module}</span></td>
                                    <td>{item.category}</td>
                                    <td>{item.page_name}</td>
                                    <td>{item.description}</td>
                                    <td>{fmtDate(item.visit_time)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </DetailModal>
            )}
        </>
    );
}

function DetailModal({ title, phone, onClose, children, maxWidth = "560px" }) {
    return (
        <div
            onClick={onClose}
            style={{
                position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 9999,
                display: "flex", alignItems: "center", justifyContent: "center", padding: "20px",
            }}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                style={{
                    background: "#fff", borderRadius: "8px", width: "100%", maxWidth,
                    maxHeight: "80vh", display: "flex", flexDirection: "column",
                    boxShadow: "0 8px 40px rgba(0,0,0,0.25)",
                }}
            >
                <div className="d-flex justify-content-between align-items-center px-4 py-3 border-bottom">
                    <div>
                        <div className="fw-bold" style={{ fontSize: "15px" }}>{title}</div>
                        <div className="text-muted" style={{ fontSize: "12px" }}>{phone}</div>
                    </div>
                    <button className="btn-close" onClick={onClose} />
                </div>
                <div style={{ overflowY: "auto", padding: "16px" }}>{children}</div>
            </div>
        </div>
    );
}

export default ParticipantsPostActivity;
